import type { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { BaseQLFieldDefinition } from '@adornis/baseql/metadata/types.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-link.js';
import '@adornis/chemistry/elements/components/x-section';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { until } from 'lit/directives/until.js';
import './tr-toggle-content';

/*
 * @element matching-user-fields
 */
@customElement('tr-entity-fields')
export class TREntityFields extends ChemistryLitElement {
  @property({ attribute: 'page-title' }) pageTitle?: string;
  @property({ attribute: false }) userClass?: typeof AdornisEntity;
  @property({ attribute: false }) fieldBlackList?: string[];

  @state() protected _translator = new TranslationController(this);

  override render() {
    if (!this.userClass)
      return html` <x-empty-placeholder .text=${'Keine Nutzerklasse gegeben'}></x-empty-placeholder> `;
    return html`
      <x-section padding="md" space="lg">
        <x-h1> ${this.pageTitle} </x-h1>
        ${this._fields()}
      </x-section>
    `;
  }

  protected _fields() {
    if (!this.userClass) return nothing;
    return html`
      <x-flex space="md">
        ${repeat(
          Object.values(this.userClass.fields ?? {}).filter(field => !this.fieldBlackList?.includes(field.name)),
          field => this._field(field),
        )}

        <x-text ${css({ alignSelf: 'center', maxWidth: this.sizes.maxReadableWidth, textAlign: 'center' })}>
          Wollen Sie Felder ändern oder neue Felder hinzufügen? Wenden Sie sich an
          <x-link href="mailto:buchungsheld@adornis.de"> buchungsheld@adornis.de </x-link>
        </x-text>
      </x-flex>
    `;
  }

  protected _field(field: BaseQLFieldDefinition) {
    const type_info = this._getTypeInfo(field);
    return html`
      <x-card space="sm" ${css({ width: '100%' })}>
        <x-flex horizontal crossaxis-center space="sm">
          <x-text bold> ${this._translator.translate('field', field.name)} </x-text>
          ${until(
            this._isFieldMandatory(field).then(isMandatory =>
              isMandatory ? html` <x-text tone="warning"> Verpflichtend </x-text> ` : nothing,
            ),
            nothing,
          )}
        </x-flex>
        <x-flex horizontal space="sm">
          ${type_info
            ? html`<x-text tone="subtle"> ${this._translator.translate('types', type_info.type)} </x-text>`
            : nothing}
          ${type_info?.values
            ? html`<tr-toggle-content .label=${html`<x-text tone="subtle"
                >${this._translator.translate('enum_values')}</x-text
              >`}>
                <x-text tone="subtle"
                  >${type_info.values
                    .map(v => {
                      const translation = this._translator.translate('enums', v);
                      if (translation == `enums.${v}`) return v;
                      return translation;
                    })
                    .join(', ')}</x-text
                </x-text>
              </tr-toggle-content>`
            : nothing}
        </x-flex>
      </x-card>
    `;
  }

  protected async _isFieldMandatory(field: BaseQLFieldDefinition) {
    if (!this.userClass) return false;
    try {
      await this.userClass.prototype.validator.validateKey({ [field.name]: '' }, field.name);
      return false;
    } catch {
      return true;
    }
  }
  protected _getTypeInfo(field: BaseQLFieldDefinition): { type: string; name: string; values?: string[] } | undefined {
    const type = field.type();
    if (type._isBaseQLEnum) {
      const values = Object.keys(type).filter(key => !key.startsWith('_'));
      return { name: type._class, values, type: 'enum' };
    } else if (type.name) {
      return { name: type.name, type: type.name.toLowerCase() };
    }
  }
}
